import { http } from '@/lib/http';
import type { ReturnOrder, ReturnStatus } from '@/types/returns';

const BASE = '/api/v1/returns';

export interface ReturnListQuery {
  status?: ReturnStatus;
  salesOrderId?: string;
  customerId?: string;
  externalReturnRef?: string;
  page?: number;
  pageSize?: number;
}

export interface ReturnListResponse {
  rows: ReturnOrder[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CreateReturnLinePayload {
  salesOrderItemId: string;
  quantity: number;
  reason?: string;
}

export interface CreateReturnPayload {
  salesOrderId: string;
  dispatchOrderId?: string;
  warehouseId?: string;
  items: CreateReturnLinePayload[];
  externalReturnRef?: string;
  reason?: string;
  notes?: string;
}

export interface UpdateReturnStatusPayload {
  status: ReturnStatus;
  reason?: string;
}

/**
 * Scan-inward — bumps a return line's `receivedQuantity` by one unit
 * (barcode mode) or by an explicit quantity. The BE rejects if neither
 * `barcode` nor (`returnOrderItemId` + `quantity`) is supplied.
 */
export interface ScanInwardPayload {
  barcode?: string;
  returnOrderItemId?: string;
  quantity?: number;
}

/**
 * QC classification — splits each line's `receivedQuantity` into good /
 * bad. Sums must not exceed `receivedQuantity`. Lines not present are
 * left unchanged.
 */
export interface ClassifyReturnLinePayload {
  returnOrderItemId: string;
  goodQuantity?: number;
  badQuantity?: number;
}

export interface ClassifyReturnPayload {
  lines: ClassifyReturnLinePayload[];
}

export const returnsService = {
  list: (query: ReturnListQuery = {}) =>
    http.get<ReturnListResponse>(BASE, { params: query }),
  /** Returns in REQUESTED state assigned to the current user. */
  myReviews: () => http.get<ReturnOrder[]>(`${BASE}/my-reviews`),
  get: (id: string) => http.get<ReturnOrder>(`${BASE}/${id}`),
  create: (payload: CreateReturnPayload) =>
    http.post<ReturnOrder, CreateReturnPayload>(BASE, payload),
  updateStatus: (id: string, payload: UpdateReturnStatusPayload) =>
    http.patch<ReturnOrder, UpdateReturnStatusPayload>(`${BASE}/${id}/status`, payload),

  // Reviewer workflow (parity with cancelsService)
  assignReviewer: (id: string, payload: { reviewerUserId: string }) =>
    http.post<ReturnOrder, { reviewerUserId: string }>(`${BASE}/${id}/assign-reviewer`, payload),
  approve: (id: string, payload: { reviewerNote?: string } = {}) =>
    http.post<ReturnOrder, { reviewerNote?: string }>(`${BASE}/${id}/review/approve`, payload),
  reject: (id: string, payload: { reviewerNote: string }) =>
    http.post<ReturnOrder, { reviewerNote: string }>(`${BASE}/${id}/review/reject`, payload),

  /**
   * Cancel a return before any goods are received. Allowed from
   * REQUESTED (pre-review pull-out) or APPROVED (post-review pull-out
   * or carrier-failed pickup). Quantity flows back to the SO line so
   * the customer can re-attempt.
   */
  cancel: (id: string, payload: { reason: string }) =>
    http.post<ReturnOrder, { reason: string }>(`${BASE}/${id}/cancel`, payload),

  /** Aftersales-3 — retry pushing the reverse pickup to the carrier. */
  retryReversePickupCarrier: (id: string) =>
    http.post<ReturnOrder>(`${BASE}/${id}/reverse-pickup/retry-carrier`),

  scanInward: (id: string, payload: ScanInwardPayload) =>
    http.post<ReturnOrder, ScanInwardPayload>(`${BASE}/${id}/scan-inward`, payload),
  classify: (id: string, payload: ClassifyReturnPayload) =>
    http.post<ReturnOrder, ClassifyReturnPayload>(`${BASE}/${id}/classify`, payload),
};

